import React, { useContext, useEffect, useRef, useState } from 'react';
import { ResourceStrings } from '../../../ResourceStrings';
import styled from 'styled-components/macro';
import { NotificationInfo } from '../../../store/appState';
import { App, SignInState } from '../../../app';
import { forMinWidth } from '../../../theme/breakpoints';
import Flex from '../../shared/Flex';
import Button from '../../shared/Button';
import { Icon } from '@fluentui/react/lib/Icon';
import ShareLinkDialog from '../../shared/ShareLinkDialog';
import { useUrlFragment } from '../../../hooks/routing';
import { ContextualMenuItem } from './RichPostDetail';
import { Permission, UserRole } from '../../../config/permissions';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { CommandButton } from '@fluentui/react/lib/Button';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { hasPermission } from '../../../config/accessPolicies';
import * as Actions from '../../../store/actions';
import ConvertCommentDialog from './ConvertCommentDialog';
import { TicketContext } from './TicketContext';
import { VoteType } from '../../../store/actions';
import { ticketNotificationKey } from '../../../store/actionsHandler';
import { addNodeOperationKey, keepFormOpen, operationIsSubmitting, ticketOperationKey, UIOperationState, useOperation } from '../../../store/operation';
import OperationNotification from './OperationNotification';
import { reactivationRules } from './reactivationRules';
import { TimelineItem, TimelineItemType } from '../../../hooks/posts';
import { hasItems } from '../../../utils/collections';
import FeedbackTimelineComponent, { ElementColor } from './FeedbackTimelineComponent';
import VisibilityTransition from '../../shared/VisibilityTransition';
import CommentEditor, { NodeEditorOperation } from './CommentEditor';
import { useHistory } from 'react-router';
import { hashForNode, hashForNodeDirect, UrlParam } from '../../../constants/urlParams';
import { ensureSignedIn, restrictUrlParams } from '../../../utils/webPage';
import { generateTrackingId } from '../../../utils/tools';
import { HashLocatorContext } from '../../shared/HashLocator';
import { ChildPost, HistoryItem, PrincipalId, RootPost } from '../../../RootPost';
import HiddenTextInfo from './HiddenTextInfo';
import HtmlPreview from '../../shared/HtmlPreview';
import { NodeType } from "./RichNodeEditor";
import { instance } from '../../../config/serverConfig';

export const commentTitleColors = (comment: ChildPost, ticket: RootPost, isNMIRequestComment: boolean): ElementColor => {
    if (isNMIRequestComment) {
        return {
            backgroundColor: "var(--color-state-need-more-info)",
            textColor: "white"
        };
    }

    return null;
}

export interface RichCommentProps {
    comment: ChildPost;
    timelineItem?: TimelineItem;
    showNMIEditor?: boolean;
    showNMIEditorClickCount?: number;
    showNeedMoreInfoLink?: boolean;
    fillMissingInfoClicked: () => void;
    idPrefix: string;
    className?: string;
    currentUserId: PrincipalId;
    onEdit?: (comment: ChildPost) => void;
}

export default function RichComment(props: RichCommentProps): JSX.Element {
    const { comment, idPrefix, className } = props;

    const isInReadOnlyMode = useSelector((state: RootState) => state.appState.isInReadOnlyMode);
    const [showLinkDialog, setShowLinkDialog] = useState(false);
    const [showConvertDialog, setShowConvertDialog] = useState(false);
    const userRoles = useSelector((state: RootState) => state.appState.userRoles);
    const userVsid = useSelector((state: RootState) => state.appState.userVsid);
    const isUserBanned = useSelector((state: RootState) => state.appState.isUserBanned);
    const [newCommentTrackingId, setNewCommentTrackingId] = useState<string>(null);
    const signInState = useSelector((state: RootState) => state.appState.signInState);
    const ticketDetailsCtx = useContext(TicketContext);
    const ticket = ticketDetailsCtx.ticketDetails.ticket?.data;
    const history = useHistory();

    const commentOpKey = ticketOperationKey(ticket?.id, comment.id);
    const commentOperation = useOperation(commentOpKey);

    const addCommentOpKey = addNodeOperationKey(ticket?.id, comment.id, "nmi");
    const addCommentOperation = useOperation(addCommentOpKey);

    const commentId = hashForNode(comment.id, idPrefix);
    const commentIdDirect = hashForNodeDirect(comment.id);   // id to reach nested node without specifying parents
    const fullUrl = useUrlFragment(commentId);
    const shareableUrl = restrictUrlParams(fullUrl, []);

    const hashLocatorCtx = useContext(HashLocatorContext);
    useEffect(() => hashLocatorCtx.newContentWasLoaded(), []);

    const isAnyOperationInProgress = commentOperation.isInProgress() || addCommentOperation.isInProgress();

    const commentMenuProps = (comment: ChildPost): ContextualMenuItem[] => [
        {
            key: "Share",
            text: "Share",
            onClick: () => setShowLinkDialog(true)
        },
        {
            key: "Edit",
            text: "Edit",
            disabled: isInReadOnlyMode || isUserBanned || isAnyOperationInProgress,
            iconProps: { iconName: 'SingleColumnEdit' },
            data: {
                requiredPermission: Permission.Comment_Edit,
            },
            onClick: () => props?.onEdit(comment)
        },
        {
            key: "flagForModeration",
            text: "Report content",
            disabled: isInReadOnlyMode || isAnyOperationInProgress,
            data: {
                requiredPermission: Permission.Comment_FlagForModeration,
            },
            onClick: function () {
                commentOperation.resetOperation();
                commentOperation.setState(UIOperationState.InProgress);
                Actions.flagForModeration(comment.id, ticket.id, commentOpKey);
            }
        },
        {
            key: "Convert",
            text: "Convert",
            disabled: isInReadOnlyMode || isUserBanned || isAnyOperationInProgress || (!comment.isPublic),
            data: {
                requiredPermission: Permission.Comment_Convert,
            },
            onClick: () => setShowConvertDialog(true)
        },
        {
            key: "Delete",
            text: "Delete",
            disabled: isInReadOnlyMode || isAnyOperationInProgress,
            data: {
                requiredPermission: Permission.Comment_Delete,
            },
            onClick: function () {
                commentOperation.resetOperation();
                commentOperation.setState(UIOperationState.InProgress);
                Actions.deleteNode(comment.id, commentOpKey);
            }
        },
    ];

    const getRolesOfCurrentUerForComment = (comment: ChildPost): UserRole[] =>
        (comment.author.id === props.currentUserId) ? [UserRole.Author] : [];

    const getPermittedComentMenuItems = (comment: ChildPost, userRoles: UserRole[]): IContextualMenuItem[] => {
        const contextSpecificRoles = getRolesOfCurrentUerForComment(comment);
        const allAvailableRoles = [...userRoles, ...contextSpecificRoles];

        return commentMenuProps(comment)
            .filter(x => hasPermission(x.data?.requiredPermission, allAvailableRoles));
    }

    const reasonCantVote = (comment: ChildPost, signInState: SignInState, voteType: VoteType): string => {
        if (voteType === comment.userVoteType) {
            return "You have already voted for this comment";
        }

        if (signInState !== SignInState.Success) {
            return ResourceStrings.authNeeded_Vote();
        }

        if (comment.author.id === props.currentUserId) {
            return ResourceStrings.youCreatedIt();
        }

        return null;
    }

    const onVoteClick = (comment: ChildPost, signInState: SignInState, voteType: VoteType): void => {
        const reason = reasonCantVote(comment, signInState, voteType);
        if (reason) {
            Actions.setContextualNotification(ticketNotificationKey(ticket?.id), {
                title: reason,
                type: "information",
                isCritical: true
            } as NotificationInfo)
        } else {
            commentOperation.resetOperation();
            commentOperation.setState(UIOperationState.InProgress);
            Actions.voteOnNode(ticket.id, commentOpKey, comment.id, voteType);
        }
    }

    const userHasVotedUp = comment.userVoteType === "voteup";
    const userHasVotedDown = comment.userVoteType === "votedown";

    // Check if current user can view this comment. Used to enable/diable voting buttons. It checks
    // - Is this not a public comment
    // - If it isn't then do I have access to it
    // First condition is not strictly needed but added it just in case some user types our private comment marker text in a public comment
    const isCommentHiddenForUser = !comment.isPublic && !comment.text;

    const reactivationRule = reactivationRules[ticket?.state];

    const associatedItem = props.timelineItem?.associatedItem;
    const associatedStateChange: HistoryItem | null = associatedItem?.type === TimelineItemType.stateChange ? associatedItem.data as HistoryItem : null;

    const dismissNMIEditor = (): void => {
        addCommentOperation.resetOperation();
        removeNMIFromUrl();
    }

    const removeNMIFromUrl = (): void => {
        const urlParams = new URLSearchParams(history.location.search);
        urlParams.delete(UrlParam.ProvideMoreInfo);
        history.replace(`${history.location.pathname}?${urlParams.toString()}`);
    }

    const refComment = useRef<HTMLDivElement>();
    useEffect(() => {
        if (props.showNMIEditor && refComment.current) {
            if (refComment.current) {
                refComment.current.scrollIntoView();
            }

            // give the editor some time to expand before scrolling to it, needed when clicking the provide more info btn at the bottom
            setTimeout(() => {
                if (refComment.current) {
                    refComment.current.scrollIntoView();
                }
            }, 800);

            if (ensureSignedIn(signInState, ticketNotificationKey(ticket?.id), "Please sign in to post comments and solutions.")) {
                setNewCommentTrackingId(generateTrackingId());
                addCommentOperation.resetOperation();
                addCommentOperation.setState(UIOperationState.InProgress);
            }
        }
    }, [props.showNMIEditor, signInState, ticket?.id, ticketDetailsCtx?.ticketDetails?.fillMissingInfoClickAttempt]);

    useEffect(() => {
        if (addCommentOperation.operation?.state === UIOperationState.Completed) {
            removeNMIFromUrl();
        }
    }, [ticket?.id, addCommentOperation.operation?.state]);

    return <>
        <div id={commentIdDirect}>
            <CommentWrapper id={commentId} ref={refComment} className={`comment ${className ?? ''} ${props.showNMIEditor ? 'show-nmi-editor' : ''}`}>
                <div className={`comment-title ${props.showNeedMoreInfoLink ? "show-more-info" : ""}`}>
                    <span>
                        <span>
                            <span>
                                <Icon iconName="Chat" />&nbsp;&nbsp;
                            </span>
                            <span><a href={App.GetUserProfileLink(comment.author.id)} target="_blank" rel="noopener noreferrer">{comment.author.name}</a>&nbsp;&nbsp;</span>
                        </span>
                        {comment.reaction === "reactYes" && <ReactionTitle>{reactivationRule?.yesTitle}</ReactionTitle>}
                        {comment.reaction === "reactNo" && <ReactionTitle>{reactivationRule?.noTitle}</ReactionTitle>}

                        {associatedStateChange && (
                            <span className="changed-state">
                                <span className={`pill post-state ${associatedStateChange.status}`}>
                                    {App.LocalizeStateName(associatedStateChange.status, ticket?.type)}
                                </span>
                            </span>
                        )}
                    </span>
                    {!isCommentHiddenForUser &&
                        <CommandButton
                            className="btn-semi-compact-more-options"
                            aria-label="Show options"
                            menuProps={{
                                items: getPermittedComentMenuItems(comment, userRoles)
                            }}
                            onRenderMenuIcon={() => <span></span>}
                            menuIconProps={{
                                hidden: true
                            }}
                        >
                            ···
                        </CommandButton>
                    }
                </div>

                {!comment.isPublic && ticket.isPublic && (
                    <div className="private-indicator" title={`Visible to: ${(comment.recipientNames ?? "")}`}>
                        <Icon iconName="LockSolid" />
                        <span>private</span>
                    </div>
                )}

                <HiddenTextInfo post={comment} currentUserId={props.currentUserId} />


                {comment.reaction !== "reactYes" && <HtmlPreview text={comment.text} isPublic={comment.isPublic} className="comment-details" />}

                <Flex alignItems="center" densityHor="relaxed" className="comment-actions-bar">
                    <span className="actions-bar-left">
                        <Flex densityHor="high">
                            <TooltipHost
                                content={userHasVotedUp ? "Click to cancel vote" : "Click to vote up"}
                                id={`voteup-${comment.id}`}
                            >
                                <Button
                                    buttonType="normal"
                                    size="compact"
                                    borderless
                                    disabled={isInReadOnlyMode || isCommentHiddenForUser}
                                    aria-describedby={`voteup-${comment.id}`}
                                    aria-label={userHasVotedUp ? "Click to cancel vote" : "Click to vote up"}
                                    onClick={() => onVoteClick(comment, signInState, userHasVotedUp ? VoteType.cancelVote : VoteType.voteUp)}
                                >
                                    <Icon iconName={userHasVotedUp ? "LikeSolid" : "Like"} />
                                </Button>
                            </TooltipHost>
                            <span aria-label={`${comment.voteCount} votes`}>{comment.voteCount}</span>

                            <TooltipHost
                                content={userHasVotedDown ? "Click to cancel vote" : "Click to vote down"}
                                id={`votedown-${comment.id}`}
                            >
                                <Button
                                    buttonType="normal"
                                    size="compact"
                                    borderless
                                    disabled={isInReadOnlyMode || isCommentHiddenForUser}
                                    aria-describedby={`votedown-${comment.id}`}
                                    aria-label={userHasVotedDown ? "Click to cancel vote" : "Click to vote down"}
                                    onClick={() => onVoteClick(comment, signInState, userHasVotedDown ? VoteType.cancelVote : VoteType.voteDown)}
                                >
                                    <Icon iconName={userHasVotedDown ? "DislikeSolid" : "Dislike"} />
                                </Button>
                            </TooltipHost>
                        </Flex>
                    </span>

                    <span className="light-text">{App.ParseDateTimeToLocal(comment.createdDate)} </span>
                </Flex>

                {props.showNeedMoreInfoLink && !props.showNMIEditor && !isUserBanned && !isInReadOnlyMode && (
                    <div className="missing-info-btn-wrapper">
                        <Button
                            buttonType="primary"
                            onClick={props.fillMissingInfoClicked}>
                            {ResourceStrings.provideMoreInfo()}
                        </Button>
                    </div>
                )}


                <OperationNotificationWrapper
                    operation={commentOperation.operation}
                    resetOperation={commentOperation.resetOperation}
                    className="comment-operation-message"
                />

                <OperationNotificationWrapper
                    operation={addCommentOperation.operation}
                    resetOperation={addCommentOperation.resetOperation}
                    className="comment-operation-message"
                />

                {/* NMI */}
                <VisibilityTransition showIf={!!props.showNMIEditor && keepFormOpen(addCommentOperation.operation)}>
                    <CommentEditorWrapper>
                        <CommentEditor
                            operation={NodeEditorOperation.Add}
                            uiOperationKey={addCommentOpKey}
                            newNodeTrackingId={newCommentTrackingId}
                            title={instance.isInternal ? "The team needs more information about this problem" : "The Visual Studio team needs more information about this problem"}
                            placeHolderText={"Enter your detailed comments here"}
                            parentNodeId={comment.id}
                            disableAddSolution
                            isProvideInfo
                            currentUserId={props.currentUserId}
                            disabled={operationIsSubmitting(addCommentOperation.operation)}
                            isSubmitting={operationIsSubmitting(addCommentOperation.operation)}
                            onCancel={() => dismissNMIEditor()}
                        />
                    </CommentEditorWrapper>
                </VisibilityTransition>
            </CommentWrapper>
        </div>

        {hasItems(props.timelineItem?.children) && (
            <FeedbackTimelineComponent idPrefix={commentId} currentUserId={props.currentUserId} timelineItems={props.timelineItem.children} parentNodeId={comment.id} />
        )}

        <ConvertCommentDialog
            ticket={ticket}
            isOpen={showConvertDialog}
            comment={comment}
            onSubmit={(convertTo: NodeType) => {
                commentOperation.resetOperation();
                commentOperation.setState(UIOperationState.InProgress);
                Actions.convertNode(ticket.id, commentOpKey, comment.id, convertTo, comment.parentId)
                setShowConvertDialog(false);
            }}
            onDismiss={() => setShowConvertDialog(false)}
        />

        <ShareLinkDialog
            title="Share a link to this comment"
            message="Copy and paste the URL below to share a direct link to this comment."
            url={shareableUrl}
            isOpen={showLinkDialog}
            onDismiss={() => setShowLinkDialog(false)} />
    </>
}

const CommentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    background-color: var(--color-background);
    padding: 0;
    border-radius: 0;

    &>div {
        display: flex;

        &:first-child {
            color: var(--color-surface-dark2);
            font-size: 1.4rem;
        }

        &> * + * {
            margin-left: .5rem;

            ${forMinWidth.md`
                margin-left: 3.5rem;
            `}
        }
    }

    && .comment-title {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        border-bottom: solid 1px var(--color-on-surface-lightest);
        padding: .4rem 2rem;
        background-color: var(--titlebar-background-color);
        min-height: var(--timeline-header-min-height);

        &, span, a, button {
            line-height: 100%;
        }

        &, span, button {
            color: var(--titlebar-text-color);
        }

        &.show-more-info a {
            color: var(--titlebar-text-color);
        }

        strong {
            color: var(--color-on-surface);
        }

        button {
            background-color: transparent;
        }

        ${forMinWidth.xs`
            align-items: center;
        `}

        > span {
            display: flex;
            flex-direction: column;

            >* {
                display: flex;
                align-items: center;
            }

            ${forMinWidth.xs`
                flex-direction: row;
            `}
        }

        i {
            margin-top: 2px;
        }

        .changed-state {
            display: flex;
            flex-direction: column;
            padding-left: 0;
            padding-right: 2rem;
            align-items: center;

            .post-state {
                font-size: 1.4rem;
            }

            ${forMinWidth.md`
                flex-direction: row;
                padding-left: 2rem;
            `}
        }

        .pill.pill {
            padding: .4rem .8rem;
            font-size: 1.2rem;
            border-radius: 0.3rem;
            color: var(--titlebar-background-color);
            font-weight: 600;
        }

        &.show-more-info .pill.pill {
            background-color: var(--titlebar-text-color);
        }
    }

    .private-indicator {
        padding-left: 2rem;

        i {
            font-size: 1.4rem;
        }

        > * + * {
            margin-left: .5rem;
        }
    }

    && .comment-details {
        display: block;
        margin-left: 0;
        word-break: break-word;
        padding: 0 2rem;
        padding-top: 0.2rem;
        max-height: 100rem;
        overflow-y: auto;

        >* {
            margin-left: 0;
        }

        p {
            margin-bottom: 1.6rem;
        }

        >:last-child {
            margin-bottom: 0;
        }
    }

    &> * + * {
        margin-top: 1rem;
    }

    .light-text {
        color: var(--color-on-background-lighter);
        font-size: 1.4rem;
    }

    .comment-actions-bar {
        display: flex;
        justify-content: space-between;
        padding: 0 1.5rem 1rem 2rem;

        span, button, a {
            font-size: 1.6rem;
        }

        a, button {
            color: var(--color-on-surface-link);
        }

        a:disabled, button:disabled {
            color: var(--color-surface-dark1);
        }

        .actions-bar-left {
            display: flex;

            &> * + * {
                margin-left: 3rem;
            }
        }
    }

    .missing-info-btn-wrapper {
        padding: 0 1rem 2rem 2rem;
    }
`;

const ReactionTitle = styled.span`
    margin: 0;
    font-weight: bold;
    color: var(--color-on-surface);

    ${forMinWidth.md`
        margin: 0 2rem;
    `}
`;

const CommentEditorWrapper = styled.div`
    border-top: solid 1px var(--color-on-surface-lightest);
    padding-top: 1rem;
    padding-left: 4rem;
    padding-right: 1rem;
    margin: 0;
    margin-bottom: 1rem;
    width: 100%
`;

const OperationNotificationWrapper = styled(OperationNotification)`
    margin: 2rem 1rem 1rem 1rem;
`;
